import * as React from 'react';
import styled, { css } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';

export interface FileInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  semanticId?: string;
  onFileSelect?: (files: FileList | null) => void;
  fileType?: string;
  label?: string;
  error?: boolean;
}

function dashedBorderGradient(color: string): string {
  return `
    repeating-linear-gradient(0deg, ${color}, ${color} 7px, transparent 7px, transparent 14px, ${color} 14px),
    repeating-linear-gradient(90deg, ${color}, ${color} 7px, transparent 7px, transparent 14px, ${color} 14px),
    repeating-linear-gradient(180deg, ${color}, ${color} 7px, transparent 7px, transparent 14px, ${color} 14px),
    repeating-linear-gradient(270deg, ${color}, ${color} 7px, transparent 7px, transparent 14px, ${color} 14px)
  `;
}

const Wrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$disabled',
})<{ $disabled: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  border-radius: var(--ui-radius-sm, ${rem(4)});
  background-color: var(--ui-upload-bg, var(--ui-surface));
  padding: ${rem(16)} ${rem(8)};
  width: 100%;
  min-height: ${rem(100)};

  background-position: 0 0, 0 0, 100% 0, 0 100%;
  background-size: 1.5px 100%, 100% 1.5px, 1.5px 100%, 100% 1.5px;
  background-repeat: no-repeat;
  background-image: ${dashedBorderGradient('var(--ui-upload-border, var(--ui-border))')};

  &.is-invalid {
    background-image: ${dashedBorderGradient('var(--ui-upload-border-error, var(--ui-status-danger))')};
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      background-color: var(--ui-background-muted);
      pointer-events: none;
      cursor: not-allowed;
      opacity: 1;
      color: var(--ui-text-subtle);

      .upload-label,
      .upload-hint,
      .material-symbols-outlined {
        color: var(--ui-text-subtle);
        cursor: not-allowed;
      }
    `}

  .uploadDetail {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    position: relative;
    gap: ${rem(5)};
    flex: 0 0 auto;

    .material-symbols-outlined {
      color: var(--ui-upload-icon-color, var(--ui-interactive));
      width: ${rem(21)};
      height: ${rem(15)};
      z-index: 2;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      flex-shrink: 0;
    }
  }

  .upload-label {
    font-family: var(--ui-font-sans);
    font-weight: 400;
    font-size: ${rem(12)};
    line-height: ${rem(14)};
    color: var(--ui-text);
  }

  .upload-hint {
    font-family: var(--ui-font-sans);
    font-weight: 400;
    font-size: ${rem(12)};
    line-height: ${rem(14)};
    color: var(--ui-text);
  }

  input[type='file'] {
    position: absolute;
    max-width: 100%;
    width: 100%;
    height: 100%;
    opacity: 0;
    top: 0;
    left: 0;
    z-index: 1;
    cursor: pointer;
  }
`;

export const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(function FileInput(
  { semanticId, onFileSelect, disabled, multiple, onChange, fileType, label, error = false, className, style, accept, ...rest },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);
  const [selectedName, setSelectedName] = React.useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    onChange?.(e);
    const list = e.target.files;
    onFileSelect?.(list ?? null);
    if (list && list.length > 0) {
      setSelectedName(list.length === 1 ? list[0].name : `${list[0].name} (+${list.length - 1} more)`);
    } else {
      setSelectedName('');
    }
  };

  const wrapperClass = [className, error ? 'is-invalid' : ''].filter(Boolean).join(' ') || undefined;

  return (
    <Wrapper
      className={wrapperClass}
      style={style}
      $disabled={Boolean(disabled)}
      data-ui-file-input=""
      data-disabled={disabled || undefined}
      aria-disabled={disabled || undefined}
      {...semanticProps}
    >
      <div className="uploadDetail">
        <span className="material-symbols-outlined">backup</span>
        <div>
          {selectedName ? (
            <span className="upload-label">{selectedName}</span>
          ) : (
            <>
              <span className="upload-label">Click to Upload or Drag or Drop</span>
              <br />
              <span className="upload-hint">
                {fileType ?? '*PNG, JPG, JPEG, PDF, DOCX (Not more than 10 MB)'}
              </span>
            </>
          )}
        </div>
      </div>
      <input
        ref={ref}
        type="file"
        disabled={disabled}
        multiple={multiple}
        accept={accept ?? '.pdf, .jpg, .jpeg, .png, .docx, .doc'}
        onChange={handleChange}
        aria-invalid={error || undefined}
        aria-disabled={disabled || undefined}
        {...rest}
      />
    </Wrapper>
  );
});

FileInput.displayName = 'FileInput';
